import { slugCompany } from './blockIds'
import type { OptimizationBlockState, ResumeOptimizationRun } from './types'

export const JD_MINOR_SIMILARITY_THRESHOLD = 0.92

export interface IncrementalSnapshot {
  jd_normalized_hash: string
  section_fingerprints: Record<string, string>
  captured_at: string
}

export interface IncrementalPlanMeta {
  incremental_enabled: boolean
  previous_run_id: string | null
  jd_similarity: number | null
  jd_minor_change: boolean
  blocks_skipped: number
  blocks_to_run: number
}

function stableJson(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJson(item)).join(',')}]`
  }
  const obj = value as Record<string, unknown>
  const keys = Object.keys(obj).sort()
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableJson(obj[k])}`).join(',')}}`
}

export function fingerprintValue(value: unknown): string {
  const json = stableJson(value)
  let hash = 2166136261
  for (let i = 0; i < json.length; i += 1) {
    hash ^= json.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return `fp_${(hash >>> 0).toString(16)}`
}

export function normalizeJd(jd: string): string {
  return jd.trim().toLowerCase().replace(/\s+/g, ' ')
}

export function jdNormalizedHash(jd: string): string {
  return fingerprintValue(normalizeJd(jd))
}

export function jdSimilarity(previousJd: string, nextJd: string): number {
  const left = normalizeJd(previousJd)
  const right = normalizeJd(nextJd)
  if (left === right) return 1
  const leftWords = new Set(left.split(' ').filter((word) => word.length > 2))
  const rightWords = new Set(right.split(' ').filter((word) => word.length > 2))
  if (leftWords.size === 0 || rightWords.size === 0) return 0
  let overlap = 0
  for (const word of leftWords) {
    if (rightWords.has(word)) overlap += 1
  }
  return overlap / Math.max(leftWords.size, rightWords.size)
}

export function wxCompanyFingerprint(workExperience: unknown, companyName: string): string {
  if (!Array.isArray(workExperience)) return fingerprintValue(null)
  const slug = slugCompany(companyName)
  for (const entry of workExperience) {
    if (!entry || typeof entry !== 'object') continue
    const name = typeof (entry as { name?: string }).name === 'string'
      ? (entry as { name: string }).name.trim()
      : ''
    if (slugCompany(name) !== slug) continue
    return fingerprintValue(entry)
  }
  return fingerprintValue(null)
}

export function buildSectionFingerprints(
  rows: Array<{ section_type: string; content: unknown }>,
  workExperience: unknown
): Record<string, string> {
  const fingerprints: Record<string, string> = {}
  for (const row of rows) {
    const sectionType = row.section_type
    if (!sectionType) continue
    fingerprints[sectionType] = fingerprintValue(row.content ?? null)
  }
  if (Array.isArray(workExperience)) {
    for (const entry of workExperience) {
      if (!entry || typeof entry !== 'object') continue
      const name = typeof (entry as { name?: string }).name === 'string'
        ? (entry as { name: string }).name.trim()
        : ''
      if (!name) continue
      fingerprints[`work_experience:${slugCompany(name)}`] = fingerprintValue(entry)
    }
  }
  return fingerprints
}

export function buildIncrementalSnapshot(
  jobDescription: string,
  sectionFingerprints: Record<string, string>
): IncrementalSnapshot {
  return {
    jd_normalized_hash: jdNormalizedHash(jobDescription),
    section_fingerprints: sectionFingerprints,
    captured_at: new Date().toISOString(),
  }
}

function previousBlockMap(run: ResumeOptimizationRun): Map<string, OptimizationBlockState> {
  return new Map(run.blocks.map((block) => [block.block_id, block]))
}

function snapshotFromRun(run: ResumeOptimizationRun): IncrementalSnapshot | null {
  const raw = run.incremental_snapshot
  if (!raw || typeof raw !== 'object') return null
  const snapshot = raw as IncrementalSnapshot
  if (!snapshot.jd_normalized_hash || !snapshot.section_fingerprints) return null
  return snapshot
}

function sectionFingerprintKey(block: OptimizationBlockState): string | null {
  if (block.section_type === 'work_experience' && block.company_name) {
    return `work_experience:${slugCompany(block.company_name)}`
  }
  return block.section_type ?? null
}

function shouldSkipBlock(params: {
  block: OptimizationBlockState
  jdMinorChange: boolean
  snapshot: IncrementalSnapshot
  sectionFingerprints: Record<string, string>
  previousBlocks: Map<string, OptimizationBlockState>
}): boolean {
  const { block, jdMinorChange, snapshot, sectionFingerprints, previousBlocks } = params
  const previous = previousBlocks.get(block.block_id)
  if (!previous || previous.status !== 'done') return false

  const fpKey = sectionFingerprintKey(block)
  if (!fpKey) return false

  const currentFp = sectionFingerprints[fpKey]
  const previousFp = snapshot.section_fingerprints[fpKey]
  if (!currentFp || !previousFp || currentFp !== previousFp) return false

  if (block.section_type === 'contact_info') {
    return true
  }

  return jdMinorChange
}

export function planIncrementalBlocks(params: {
  blocks: OptimizationBlockState[]
  jobDescription: string
  previousRun: ResumeOptimizationRun | null
  sectionFingerprints: Record<string, string>
}): { blocks: OptimizationBlockState[]; meta: IncrementalPlanMeta } {
  const { blocks, jobDescription, previousRun, sectionFingerprints } = params
  const emptyMeta: IncrementalPlanMeta = {
    incremental_enabled: false,
    previous_run_id: null,
    jd_similarity: null,
    jd_minor_change: false,
    blocks_skipped: 0,
    blocks_to_run: blocks.length,
  }

  if (!previousRun?.quality_eval?.primary_pass) {
    return { blocks, meta: emptyMeta }
  }

  const snapshot = snapshotFromRun(previousRun)
  if (!snapshot) {
    return { blocks, meta: emptyMeta }
  }

  const similarity = jdSimilarity(previousRun.job_description, jobDescription)
  const jdMinorChange =
    similarity >= JD_MINOR_SIMILARITY_THRESHOLD ||
    jdNormalizedHash(previousRun.job_description) === jdNormalizedHash(jobDescription)

  const previousBlocks = previousBlockMap(previousRun)
  let blocksSkipped = 0

  const planned = blocks.map((block) => {
    if (
      shouldSkipBlock({
        block,
        jdMinorChange,
        snapshot,
        sectionFingerprints,
        previousBlocks,
      })
    ) {
      blocksSkipped += 1
      return {
        ...block,
        status: 'skipped' as const,
        duration_ms: 0,
        agent_feedback: {
          verdict:
            'Skipped: section unchanged since last successful optimization (incremental re-opt).',
          skipped: true,
          skip_reason: jdMinorChange ? 'unchanged_section_minor_jd' : 'unchanged_section',
        },
      }
    }
    return block
  })

  return {
    blocks: planned,
    meta: {
      incremental_enabled: true,
      previous_run_id: previousRun.id,
      jd_similarity: similarity,
      jd_minor_change: jdMinorChange,
      blocks_skipped: blocksSkipped,
      blocks_to_run: planned.length - blocksSkipped,
    },
  }
}
